import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Col,
    Row,
    Card,
    CardHeader,
} from "reactstrap";
import ReportDetailsHeader from "../../../Components/ReportDetailsHeader";
import { Formik } from "formik";
import * as Yup from "yup";
import apiGlobal from "../../../global/api.global";
import {
    PortConstant,
    ReportingEventName,
    ReportingEvents,
    ReportingTypeConstant,
    ReportingTypes,
} from "../../../shared/constants";
import {
    checkValidObject,
    compareObjectIgnoreType,
    dateTimeFormat,
    dynamicSetFieldErrors,
    handleServerResponse,
    useSafeFormikContext,
} from "GenericForms/Helper";
import "../../../global/GlobalCSS.css";
import { useQuery } from "react-query";
import { queryKeyes } from "shared/queryKeys";
import { Get } from "VesselMaster/vesselMaster.hooks";
import { queryClient } from "react-query/queryClient";
import env from "environment_system/env_system";
import { commonValidationMessages } from "Components/ValidationErrorMessages";
import { useDocumentTitle } from "Components/useDocument.hooks";
import ETADComponentStructure from "./ETADComponentStructure";
import { RootState } from "index";
import { useSelector, useDispatch } from "react-redux";
import { getCurrentReportAction } from "Store/Generic/ReportingSlice";
import FormLoading from "Components/FormLoading";
import ErrorComponent from "Components/ErrorComponent";

interface ETADComponentForm {
    toggleTab?: any;
    activeTab?: number;
    setErrorMessage?: any;
    editReportBool?: boolean;
    setPreviousTabIndex?: any
}

const ETADComponent = ({
    toggleTab,
    activeTab,
    setErrorMessage,
}: ETADComponentForm) => {
    /**
     * Fields availability as per conditions
     */
    const fields = useMemo(() => {
        const initialFields = {
            arrival_port: false,
            is_arrival_port_in_eca: false,
            expected_time_of_arrival_at_eca: false,
            masters_eta_at_pilot_station: false,
            departure_port: false,
            master_etd_from_present_port: false,
            is_departure_port_in_eca: false,
            expected_time_of_depature_from_eca: false,
            vessel_transit: true,
            date_and_time_of_arrival_at_eca: false,
            date_and_time_of_departure_from_eca: false,
        };
        // ... any additional logic for initializing fields ...
        return initialFields;
    }, []);

    /** State variables */
    useDocumentTitle("Expected Time of Arrival/Departure Report - ecoSAIL");
    const { VesselID, ReportID, VoyageID, CurrentReport, PreviousReport, reportingLoading, reportingError } = useSelector((state: RootState) => state.Reporting);
    const dispatch = useDispatch();
    const [otherArrPort, setOtherArrPort] = useState(false);
    const [otherDepPort, setOtherDepPort] = useState(false);
    const [fieldAvailability, setFieldAvailability] = useState(fields);
    const [addOtherArrPort, setAddOtherArrPort] = useState(false);
    const [addOtherDepPort, setAddOtherDepPort] = useState(false);
    const [vesselTransitVisibilty, setVesselTransitVisibilty] = useState<Boolean>(false)
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const formikContext = useSafeFormikContext<any>();
    const editValues = formikContext?.values;
    const setEditValues = formikContext?.setValues;
    const editInitialValues = formikContext?.initialValues;
    const editSubmit = formikContext?.submitForm;
    const editIsSubmitting = formikContext?.isSubmitting;
    const ETADCurrentReport = CurrentReport?.data?.deck_officer?.expected_time_of_arrival ?? null;
    const currentVesselReport = CurrentReport?.data?.vessel_reporting_information ?? null;
    const previousVesselReport = PreviousReport?.data?.vessel_reporting_information ?? null;
    /** State variables end */

    /** Queries start */
    /** Other Ports from master */
    const {
        data: OtherPortsObject,
        isLoading: OtherPortsLoading,
    }: { data: any; isLoading: any; isError: any } = useQuery(
        [queryKeyes.masters.OtherPortMaster.key, VesselID, ReportID],
        async () => {
            return await Get(queryKeyes.masters.OtherPortMaster.url());
        },
        { staleTime: Infinity }
    );
    /** Queries end */

    /** Assign values to initial object of ETAD */
    const getInitialETAD = () => {
        if (editInitialValues && editInitialValues?.deck_officer?.expected_time_of_arrival?.id > 0) {
            return editInitialValues?.deck_officer?.expected_time_of_arrival;
        }
        if (checkValidObject(ETADCurrentReport)) {
            return ETADCurrentReport;
        } else {
            return {
                masters_eta_at_pilot_station: null,
                expected_time_of_arrival_at_eca: null,
                expected_time_of_depature_from_eca: null,
                is_arrival_port_in_eca: false,
                master_etd_from_present_port: null,
                is_departure_port_in_eca: false,
                vessel_transit: false,
                date_and_time_of_arrival_at_eca: null,
                date_and_time_of_departure_from_eca: null,
                arrival_port: null,
                other_arrival_port: null,
                other_arrival_port_name: null as string,
                other_arrival_port_country: null,
                departure_port: null,
                other_departure_port: null,
                other_departure_port_name: null as string,
                other_departure_port_country: null,
                vessel: VesselID,
                voyage_information: VoyageID,
                vessel_reporting_information: ReportID,
            };
        }
    };

    /** UseEffect */
    useEffect(() => {
        ETADFormik.initialValues = { getInitialETAD };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ETADCurrentReport]);

    const ETADFormik = {
        initialValues: getInitialETAD(),
        validationSchema: Yup.object().shape({
            masters_eta_at_pilot_station: Yup.date().when(
                "$fieldAvailability",
                (field: any, schema) => {
                    return !fieldAvailability.masters_eta_at_pilot_station
                        ? schema.nullable()
                        : schema.min(
                            new Date(dateTimeFormat(previousVesselReport?.local_time)),
                            `${commonValidationMessages.minDateLastRecord} (${dateTimeFormat(previousVesselReport?.local_time, false, false, true)})`
                        ).required(commonValidationMessages.required);
                }
            ),
            is_arrival_port_in_eca: Yup.boolean(),
            master_etd_from_present_port: Yup.date().when(
                "$fieldAvailability",
                (field: any, schema) => {
                    return !fieldAvailability.master_etd_from_present_port
                        ? schema.nullable()
                        : schema.min(
                            new Date(dateTimeFormat(previousVesselReport?.local_time)),
                            `${commonValidationMessages.minDateLastRecord} (${dateTimeFormat(previousVesselReport?.local_time, false, false, true)})`
                        ).required(commonValidationMessages.required);
                }
            ),
            expected_time_of_depature_from_eca: Yup.date().when(
                "is_departure_port_in_eca",
                {
                    is: true,
                    then: (schema: any) =>
                        schema.min(
                            new Date(dateTimeFormat(previousVesselReport?.local_time)),
                            `${commonValidationMessages.minDateLastRecord} (${dateTimeFormat(previousVesselReport?.local_time, false, false, true)})`
                        ).required(commonValidationMessages.required),
                    otherwise: (schema: any) => schema.nullable(),
                }
            ),
            expected_time_of_arrival_at_eca: Yup.date().when(
                "is_arrival_port_in_eca",
                {
                    is: true,
                    then: (schema: any) =>
                        schema.min(
                            new Date(dateTimeFormat(previousVesselReport?.local_time)),
                            `${commonValidationMessages.minDateLastRecord} (${dateTimeFormat(previousVesselReport?.local_time, false, false, true)})`
                        ).required(commonValidationMessages.required),
                    otherwise: (schema: any) => schema.nullable(),
                }
            ),
            is_departure_port_in_eca: Yup.boolean(),
            vessel_transit: Yup.boolean(),
            date_and_time_of_arrival_at_eca: Yup.date().when(
                "vessel_transit",
                {
                    is: true,
                    then: (schema: any) =>
                        schema.min(
                            new Date(dateTimeFormat(previousVesselReport?.local_time)),
                            `${commonValidationMessages.minDateLastRecord} (${dateTimeFormat(previousVesselReport?.local_time, false, false, true)})`
                        ).required(commonValidationMessages.required),
                    otherwise: (schema: any) => schema.nullable(),
                }
            ),
            date_and_time_of_departure_from_eca: Yup.date().when(
                ["vessel_transit", "date_and_time_of_arrival_at_eca"],
                {
                    is: (vessel_transit: boolean) => vessel_transit === true,
                    then: (schema: any) =>
                        schema
                            .min(
                                new Date(dateTimeFormat(previousVesselReport?.local_time)),
                                `${commonValidationMessages.minDateLastRecord} (${dateTimeFormat(previousVesselReport?.local_time, false, false, true)})`
                            )
                            .when("date_and_time_of_arrival_at_eca", {
                                is: (arrival_date: Date) => arrival_date != null,
                                then: (schema: any) =>
                                    schema.min(
                                        Yup.ref("date_and_time_of_arrival_at_eca"),
                                        "Departure date must be after arrival date"
                                    ),
                            })
                            .required(commonValidationMessages.required),
                    otherwise: (schema: any) => schema.nullable(),
                }
            ),
            arrival_port: Yup.number().when(
                "$fieldAvailability",
                (field: any, schema) => {
                    return !fieldAvailability.arrival_port
                        ? schema.nullable()
                        : schema.required(commonValidationMessages.required);
                }
            ),
            departure_port: Yup.number().when(
                "$fieldAvailability",
                (field: any, schema) => {
                    return !fieldAvailability.departure_port
                        ? schema.nullable()
                        : schema.required(commonValidationMessages.required);
                }
            ),
            other_arrival_port: Yup.string().when(
                "$fieldAvailability",
                (field: any, schema) => {
                    return !(
                        otherArrPort === true &&
                        OtherPortsObject
                    )
                        ? schema.nullable()
                        : schema.required(commonValidationMessages.required);
                }
            ),
            other_arrival_port_name: Yup.string().when(
                "$fieldAvailability",
                (field: any, schema) => {
                    return !(addOtherArrPort === true || !OtherPortsObject)
                        ? schema.nullable()
                        : schema
                            .required(commonValidationMessages.required)
                            .min(2, "Must be at least 2 characters")
                            .max(200, "Must be at most 200 characters");
                }
            ),

            other_arrival_port_country: Yup.number().when(
                "$fieldAvailability",
                (field: any, schema) => {
                    return !(addOtherArrPort === true || !OtherPortsObject)
                        ? schema.nullable()
                        : schema.required(commonValidationMessages.required);
                }
            ),
            other_departure_port: Yup.string().when(
                "$fieldAvailability",
                (field: any, schema) => {
                    return !(
                        otherDepPort === true &&
                        OtherPortsObject
                    )
                        ? schema.nullable()
                        : schema.required(commonValidationMessages.required);
                }
            ),
            other_departure_port_name: Yup.string().when(
                "$fieldAvailability",
                (field: any, schema) => {
                    return !(addOtherDepPort === true || !OtherPortsObject)
                        ? schema.nullable()
                        : schema.required(commonValidationMessages.required)
                            .min(2, "Must be at least 2 characters")
                            .max(200, "Must be at most 200 characters");
                }
            ),
            other_departure_port_country: Yup.number().when(
                "$fieldAvailability",
                (field: any, schema) => {
                    return !(addOtherDepPort === true || !OtherPortsObject)
                        ? schema.nullable()
                        : schema.required(commonValidationMessages.required);
                }
            ),
            vessel: Yup.number(),
            voyage_information: Yup.number(),
            vessel_reporting_information: Yup.number(),
        })
    };

    const availableFields = useCallback(() => {
        if (currentVesselReport && currentVesselReport?.reporting_type === ReportingTypes.IN_PORT) {
            if ((previousVesselReport?.reporting_type === ReportingTypeConstant?.ATSEA) &&
                (currentVesselReport?.additional_events) &&
                (currentVesselReport?.reporting_event !== ReportingEvents?.EOSP)) {
                fields.arrival_port = true;
                fields.masters_eta_at_pilot_station = true;
                fields.is_arrival_port_in_eca = true;
                fields.expected_time_of_arrival_at_eca = true;
            }
            fields.departure_port = true;
            fields.master_etd_from_present_port = true;
            fields.is_departure_port_in_eca = true;
            fields.expected_time_of_depature_from_eca = true;
        } else if (
            currentVesselReport &&
            currentVesselReport?.reporting_event_name === ReportingEventName.BEGINING_SEA_PASSAGE
        ) {
            fields.arrival_port = true;
            fields.masters_eta_at_pilot_station = true;
            fields.is_arrival_port_in_eca = true;
            fields.expected_time_of_arrival_at_eca = true;
            fields.departure_port = true;
            fields.date_and_time_of_departure_from_eca = true;
        } else if (
            currentVesselReport &&
            currentVesselReport?.reporting_type === ReportingTypes.AT_SEA &&
            currentVesselReport?.reporting_event_name !== ReportingEventName.BEGINING_SEA_PASSAGE
        ) {
            fields.arrival_port = true;
            fields.masters_eta_at_pilot_station = true;
            fields.is_arrival_port_in_eca = true;
            fields.expected_time_of_arrival_at_eca = true;
        }
        setFieldAvailability(fields);
    }, [fields, currentVesselReport, previousVesselReport]);

    useEffect(() => {
        availableFields();
        setRefreshKey(refreshKey + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableFields]);
    useEffect(() => {
        if (checkValidObject(ETADCurrentReport) || editInitialValues?.deck_officer?.expected_time_of_arrival?.id) {
            if (ETADCurrentReport?.arrival_precedence_id === PortConstant.OTHER ||
                editInitialValues?.deck_officer?.expected_time_of_arrival?.arrival_precedence_id === PortConstant.OTHER
            ) {
                setOtherArrPort(true);
            } else if (
                ETADCurrentReport?.departure_precedence_id === PortConstant.OTHER ||
                editInitialValues?.deck_officer?.expected_time_of_arrival?.departure_precedence_id === PortConstant.OTHER
            ) {
                setOtherDepPort(true);
            }
            setRefreshKey(refreshKey + 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ETADCurrentReport]);

    return (
        <Card className="deck-card-body mb-0 border-0">
            <CardHeader className="px-0 py-2">
                <div className="text-center">
                    <Row>
                        <Col>
                            <h4 className="page_title pos-start mb-0">
                                Expected Time of Arrival/Departure
                            </h4>
                            <p className="card-title-desc pos-start">
                                All readings since last report
                            </p>
                        </Col>
                        <Col>
                            <ReportDetailsHeader />
                        </Col>
                    </Row>
                </div>
            </CardHeader>
            {(reportingLoading || OtherPortsLoading) && <FormLoading />}
            {reportingError && <ErrorComponent message="Error loading required data!" />}
            {!(reportingLoading || OtherPortsLoading) && !reportingError && (
                <Formik
                    key={refreshKey}
                    initialValues={ETADFormik.initialValues}
                    validationSchema={env?.form_validation === true ? ETADFormik.validationSchema : null}
                    enableReinitialize={true}
                    onSubmit={async (
                        values: any,
                        actions: { setSubmitting: (arg: boolean) => void }
                    ) => {
                        const responseArray: any = [];
                        if (env?.form_validation === false || compareObjectIgnoreType(ETADFormik.initialValues, values)) {
                            setErrorMessage(null)
                            toggleTab(activeTab + 1);
                            return;
                        }
                        actions.setSubmitting(true);
                        if (checkValidObject(ETADCurrentReport) && values?.id > 0) {
                            responseArray.push(apiGlobal.put(`expected_time_of_arrival/${values.id}/`, values))
                        } else {
                            if (values.vessel_transit === false) {
                                values.date_and_time_of_arrival_at_eca = null;
                                values.date_and_time_of_departure_from_eca = null;
                            }
                            responseArray.push(apiGlobal.post(`expected_time_of_arrival/`, values))
                        }
                        await handleServerResponse(responseArray).then(async (res) => {
                            if (res === true) {
                                dispatch(getCurrentReportAction(VesselID, ReportID));
                                if ((addOtherArrPort || addOtherDepPort) && OtherPortsObject &&
                                    OtherPortsObject?.filter((port: any) =>
                                        port?.port_name === values?.other_port_name)?.length === 0) {
                                    await queryClient.invalidateQueries(queryKeyes.masters.OtherPortMaster.key);
                                }
                                if (env?.form_validation === true) {
                                    setErrorMessage(null)
                                    toggleTab(activeTab + 1);
                                }
                            } else {
                                setErrorMessage(res)//to show api error in table
                                dynamicSetFieldErrors(res, actions, false)// to show api errror in tooltip
                            }
                            actions.setSubmitting(false);
                        })
                        actions.setSubmitting(false);
                    }}
                >
                    {() => (
                        <ETADComponentStructure
                            toggleTab={toggleTab}
                            setErrorMessage={setErrorMessage}
                            setRefreshKey={setRefreshKey}
                            refreshKey={refreshKey}
                            setVesselTransitVisibilty={setVesselTransitVisibilty}
                            vesselTransitVisibilty={vesselTransitVisibilty as boolean}
                            fieldAvailability={fieldAvailability}
                            addOtherArrPort={addOtherArrPort}
                            addOtherDepPort={addOtherDepPort}
                            otherArrPort={otherArrPort}
                            OtherPortsObject={OtherPortsObject}
                            setAddOtherDepPort={setAddOtherDepPort}
                            setOtherDepPort={setOtherDepPort}
                            setAddOtherArrPort={setAddOtherArrPort}
                            setOtherArrPort={setOtherArrPort}
                            otherDepPort={otherDepPort}
                            activeTab={activeTab}
                            editValues={editValues}
                            setEditValues={setEditValues}
                            editInitialValues={editInitialValues}
                            editSubmit={editSubmit}
                            editIsSubmitting={editIsSubmitting}
                        />
                    )}
                </Formik>
            )}
        </Card>
    );
};

export default ETADComponent;
